import { ref, onMounted, inject } from 'vue'
import { defineStore, storeToRefs } from 'pinia'
import { RequestService } from '../services/RequestService'
import navigationMonitor from '../services/NavigationMonitorService'
import { useSessionStorageStore } from './SessionStorage'
import { isEmpty, get, filter, uniqBy } from 'lodash'
import { stringMapValues } from '../utils'
import { useEmployerStore } from './EmployerStore'
import { useAuthStore } from './AuthStore'
import { useTransformOwnerContext } from '../composables/useTransformOwnerContext'
import { PERMISSION_KEYS } from '../access/permissionKeys'
const routes = {
  Home: {
    title: 'Home',
    name: 'home',
    // Every limited-access persona has a Home: transform-only users get the library-style
    // layout with the Content Library cards switched off. This single gate drives three
    // surfaces — navigate(), the breadcrumb ancestor filter, and the leading Home step in
    // BreadCrumbs.vue — so omitting transform.access made the navbar's Home tab a dead
    // click and hid the breadcrumb's Home affordance at the same time.
    permissions: [
      PERMISSION_KEYS.CORE_ACCESS,
      PERMISSION_KEYS.CONTENT_LIBRARY_ACCESS,
      PERMISSION_KEYS.TRANSFORM_ACCESS
    ]
  },
  ListOfEmployers: {
    title: 'List of Employers',
    name: 'listOfEmployers'
  },
  ActionCenter: {
    title: 'Book Overview & Action Center',
    name: 'actionCenter',
    breadCrumbs: ['home']
  },
  DetailEmployer: {
    title: 'Employer Detail',
    name: 'detailEmployer:planYearId',
    children: [
      {
        title: 'Employer Dashboard',
        name: 'employerDashboard', // 0
        breadCrumbs: ['listOfEmployers']
      },
      {
        title: 'Plan Questionnaire',
        name: 'planQuestionnaire', // 1
        breadCrumbs: ['listOfEmployers']
      },
      {
        title: 'Assessment Tool',
        name: 'assessmentTool', // 2
        breadCrumbs: ['listOfEmployers']
      },
      {
        title: 'Gap Report',
        name: 'gapReport', // 3
        breadCrumbs: ['listOfEmployers']
      },
      {
        title: 'Planning Tool',
        name: 'planningTool', // 4
        breadCrumbs: ['listOfEmployers']
      },
      {
        title: 'Calendar',
        name: 'bcsCalendar', // 5
        breadCrumbs: ['listOfEmployers']
      },
      {
        title: 'Notes',
        name: 'Notes', // 6
        breadCrumbs: ['listOfEmployers']
      }
    ]
  },
  helpAndResources: {
    title: 'Help & Resources Center',
    name: 'helpAndResources',
    breadCrumbs: ['${previousEmployerPage}'],
    permissions: [PERMISSION_KEYS.CORE_ACCESS]
  },
  AdminCenter: {
    title: 'Administrative Center',
    name: 'adminCenter',
    breadCrumbs: ['home'],
    permissions: [PERMISSION_KEYS.ADMIN_CENTER_ACCESS]
  },
  frequentlyQuestions: {
    title: 'Frequently Asked Questions',
    name: 'frequentlyQuestions',
    breadCrumbs: ['helpAndResources'],
    // Transform-only users reach their own FAQ section here; BcsFaqs restricts the page to
    // the caller's module, so widening this gate cannot expose another module's questions.
    permissions: [
      PERMISSION_KEYS.CORE_ACCESS,
      PERMISSION_KEYS.CONTENT_LIBRARY_ACCESS,
      PERMISSION_KEYS.TRANSFORM_ACCESS
    ]
  },
  glossaryTerms: {
    title: 'Glossary Terms',
    name: 'glossaryTerms',
    breadCrumbs: ['helpAndResources'],
    permissions: [PERMISSION_KEYS.CORE_ACCESS]
  },
  transformLearningDashboard: {
    title: 'BCS Transform Learning Dashboard',
    name: 'transformLearningDashboard',
    breadCrumbs: ['helpAndResources'],
    permissions: [PERMISSION_KEYS.TRANSFORM_ACCESS]
  },
  transformModuleDetail: {
    title: 'Category',
    name: 'transformModuleDetail',
    breadCrumbs: ['helpAndResources', 'transformLearningDashboard'],
    permissions: [PERMISSION_KEYS.TRANSFORM_ACCESS]
  },
  transformLessonDetail: {
    title: 'Lesson',
    name: 'transformLessonDetail',
    breadCrumbs: ['helpAndResources', 'transformLearningDashboard', 'transformModuleDetail'],
    permissions: [PERMISSION_KEYS.TRANSFORM_ACCESS]
  },
  transformSearchResults: {
    title: 'Search Results',
    name: 'transformSearchResults',
    breadCrumbs: ['helpAndResources', 'transformLearningDashboard'],
    permissions: [PERMISSION_KEYS.TRANSFORM_ACCESS]
  },
  transformQuizResultsBreakdown: {
    title: 'Results Breakdown',
    name: 'transformQuizResultsBreakdown',
    breadCrumbs: ['helpAndResources', 'transformLearningDashboard', 'transformModuleDetail', 'transformLessonDetail'],
    permissions: [PERMISSION_KEYS.TRANSFORM_ACCESS]
  },
  contentLibraryEmbeddedTool: {
    title: 'Content Library Tool',
    name: 'contentLibraryEmbeddedTool',
    breadCrumbs: ['helpAndResources', 'contentLibrary'],
    permissions: [PERMISSION_KEYS.CORE_ACCESS, PERMISSION_KEYS.CONTENT_LIBRARY_ACCESS]
  },
  termsAndConditions: {
    title: 'Terms & Conditions',
    name: 'termsAndConditions',
    breadCrumbs: ['${previousPage}']
  },
  memberProfile: {
    title: 'Member Profile',
    name: 'memberProfile',
    breadCrumbs: ['${previousPage}']
  },
  contentLibrary: {
    title: 'BCS Content Library',
    name: 'contentLibrary',
    breadCrumbs: ['helpAndResources'],
    // Core users without contentLibrary.access still enter to see the locked/upsell
    // state, so core.access also satisfies this gate.
    permissions: [PERMISSION_KEYS.CORE_ACCESS, PERMISSION_KEYS.CONTENT_LIBRARY_ACCESS]
  },
  bcsContentLibraryTaxonomyItems: {
    title: 'BCS Content Library Taxonomy Items',
    name: 'bcsContentLibraryTaxonomyItems',
    breadCrumbs: ['${previousEmployerPage}', '${previousPage}'],
    permissions: [PERMISSION_KEYS.CORE_ACCESS, PERMISSION_KEYS.CONTENT_LIBRARY_ACCESS]
  }
}

const getCompleteRouteData = (newPage) => {
  for (const mainRoute in routes) {
    if (newPage === routes[mainRoute].name) {
      return { ...routes[mainRoute], page: routes[mainRoute].name }
    }
    const children = get(routes, `${mainRoute}.children`, [])
    if (!isEmpty(children)) {
      const child = children.find((ch) => ch.name === newPage)
      if (child) {
        return { ...child, subPage: child.name, page: routes[mainRoute].name }
      }
    }
  }
  return {}
}

const getBreadCrumbs = (newPage, params) => {
  if (!newPage) return []
  const completeRouteData = getCompleteRouteData(newPage)
  let addEmployerList = false

  const breadCrumbs = get(completeRouteData, 'breadCrumbs', []).map((page) => {
    if (page.indexOf('$') !== -1) {
      let title = ''
      let name = stringMapValues(page, params)
      let subPage = ''
      let permissions

      // If name is a composite path then we parse its value to include the employer name and the formatted subpath name
      if (typeof name === 'string' && name.indexOf('/') != -1) {
        subPage = name.substring(name.indexOf('/') + 1, name.length)
        title = subPage.charAt(0).toUpperCase() + subPage.slice(1)
        title = params.actualEmployer?.value.name + ' ' + title
        title = title.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, (str) => str.toUpperCase())
        // addEmployerList = get(completeRouteData, 'name', '') === routes.termsAndConditions.name
        addEmployerList = true
      } else if (page === '${previousPage}' && name !== 'home') {
        const previousRouteData = getCompleteRouteData(name)
        title = get(previousRouteData, 'title', '')
        permissions = get(previousRouteData, 'permissions')
      }
      return {
        title,
        name,
        subPage,
        page: name,
        permissions
      }
    }
    return getCompleteRouteData(page)
  })

  completeRouteData.isDisabled = true
  let newBreadCrumbs = [
    ...(addEmployerList ? [routes.ListOfEmployers] : []),
    ...breadCrumbs,
    completeRouteData
  ]
  newBreadCrumbs = uniqBy(filter(newBreadCrumbs, (bc) => !!bc.title), 'name')
  // Ancestors the user cannot navigate to (same declarative gate navigate() enforces)
  // are dropped from the trail — e.g. Help & Resources for contentLibrary-only users.
  const authStore = useAuthStore()
  newBreadCrumbs = newBreadCrumbs.filter(
    (bc) => !bc.permissions?.length || authStore.hasAnyPermission(bc.permissions)
  )
  return newBreadCrumbs
}

export const useNavigationStore = defineStore('navigation', () => {
  const sessionStorageStore = useSessionStorageStore()
  const employerStore = useEmployerStore()
  const emitter = inject('emitter')

  const { setEvent, getEvent } = sessionStorageStore
  const { actualEmployer } = storeToRefs(employerStore)

  const { createLog, handleErrors } = RequestService()
  const isSubpage = ref(false)

  const currentPage = ref(routes.Home.name)
  const currentSubPage = ref(null)
  const backStack = ref([])
  const backStackBreadCrumbs = ref([])
  const backStackParams = ref([])
  const breadCrumbs = ref([])
  const forwardStack = ref([])
  const forwardStackBreadCrumbs = ref([])
  const forwardStackParams = ref([])
  const internalQuery = ref({})
  const navigationParams = ref({ planYearId: 0 })

  const refNavigate = ref(false)

  // Lets the navigation monitor attach the virtual-router state to every
  // Sentry anomaly event without importing this store from the service.
  navigationMonitor.registerStateProvider(() => ({
    currentPage: currentPage.value,
    currentSubPage: currentSubPage.value,
    isSubpage: isSubpage.value,
    backStackDepth: backStack.value.length,
    forwardStackDepth: forwardStack.value.length
  }))

  const checkIfRefresh = () => {
    const wasReloaded = getEvent('pageReloaded')
    if (wasReloaded === 'true') {
      setEvent('refresh', true)
    } else {
      setEvent('refresh', false)
    }
    setEvent('pageReloaded', true)
  }

  onMounted(() => {
    checkIfRefresh()
  })

  function getDynamicRoute(newPage, params = {}) {
    const keysOfParams = Object.keys(params)
    keysOfParams.forEach((key) => {
      if (newPage.includes(`:${key}`)) {
        newPage = newPage.replace(`:${key}`, params[key])
      }
    })
    return newPage
  }

  const ASSESSMENT_TOOL_VIEWED = '11'
  const PLANNING_GUIDE_VIEWED = '12'
  const CALENDAR_VIEWED = '13'
  const FAQ_VIEWED = '17'
  const GLOSSARY_VIEWED = '18'

  // NOTE: BCS Transform deliberately logs NO user-activity events (per product/lead
  // decision). Do not add a transformLearningDashboard entry here — an eventId would
  // fire createLog on navigation. It previously used '19', which collides with the
  // backend's ASSESSMENT_COMPLETED and caused a 500 on the logging gateway.
  const eventIdByPageName = new Map([
    ['assessmentTool', ASSESSMENT_TOOL_VIEWED],
    ['planningTool', PLANNING_GUIDE_VIEWED],
    ['bcsCalendar', CALENDAR_VIEWED],
    ['frequentlyQuestions', FAQ_VIEWED],
    ['glossaryTerms', GLOSSARY_VIEWED]
  ])

  // Persists the Transform navigation params a view needs to rebuild itself after a full
  // page refresh (navigationParams live only in memory). Only overwrites a stored value
  // when the matching param is actually provided: navigating via a breadcrumb omits e.g.
  // moduleId, and overwriting it with null used to break the F5 restore of that view.
  function persistTransformNavEvents(page, params) {
    const persist = (key, value) => {
      if (value !== undefined) setEvent(key, value)
    }
    if (page === routes.transformModuleDetail.name) {
      persist('transformModuleId', params.moduleId)
      persist('transformModuleTitle', params.moduleTitle)
    }
    if (page === routes.transformLessonDetail.name) {
      persist('transformLessonId', params.lessonId)
      persist('transformLessonTitle', params.lessonTitle)
      persist('transformModuleId', params.moduleId)
      persist('transformModuleTitle', params.moduleTitle)
    }
    if (page === routes.transformSearchResults.name) {
      persist('transformSearchTerm', params.searchTerm)
      persist('transformSearchOrigin', params.searchOrigin)
      persist('transformSearchOriginModuleId', params.searchOriginModuleId)
      persist('transformSearchOriginModuleTitle', params.searchOriginModuleTitle)
    }
    // Results Breakdown shares the lesson/module context keys — the `route` event
    // disambiguates which view to restore, so a refresh here rebuilds the breakdown.
    if (page === routes.transformQuizResultsBreakdown.name) {
      persist('transformLessonId', params.lessonId)
      persist('transformLessonTitle', params.lessonTitle)
      persist('transformModuleId', params.moduleId)
      persist('transformModuleTitle', params.moduleTitle)
    }
  }

  const TRANSFORM_ROUTE_NAMES = new Set([
    routes.transformLearningDashboard.name,
    routes.transformModuleDetail.name,
    routes.transformLessonDetail.name,
    routes.transformSearchResults.name,
    routes.transformQuizResultsBreakdown.name
  ])

  // Admin "view-as": the owner context is module-wide, so it is seeded/cleared at this
  // single choke point instead of per view. An explicit `owner` param enters (truthy) or
  // exits (null/'') the context; an ABSENT owner leaves it untouched so it carries across
  // Transform-internal navigation. Leaving the Transform module always exits — the context
  // (and its sessionStorage event, which drives the F5 restore) must never outlive the
  // module, or an admin would land back in another user's data hours later.
  function syncTransformOwnerContext(routeName, params) {
    const ownerContext = useTransformOwnerContext()
    if (!TRANSFORM_ROUTE_NAMES.has(routeName)) {
      ownerContext.exit()
      return
    }
    if (Object.prototype.hasOwnProperty.call(params, 'owner')) {
      if (params.owner) ownerContext.enter(params.owner)
      else ownerContext.exit()
    }
  }

  // `replace: true` swaps the current entry in place — it does NOT push the previous page
  // onto the back stack nor clear the forward stack. Used for permission-driven redirects
  // (e.g. the transform-only entry point) so a gated page the user can never reach is not
  // left in history for the browser Back button to land on.
  function navigate({ newPage, subPage = null, params = {}, query = {}, saveEvent = false, replace = false }) {
    const targetRouteData = getCompleteRouteData(newPage)
    if (!targetRouteData) {
      return
    }
    if (targetRouteData.permissions) {
      const authStore = useAuthStore()
      if (!authStore.hasAnyPermission(targetRouteData.permissions)) {
        return
      }
    }

    syncTransformOwnerContext(newPage, params)

    setEvent('pageReloaded', false)
    emitter.emit('closeBcsModal')
    document.body.style.overflow = ''
    document.body.style.paddingRight = ''

    if (params.planYearId) {
      setEvent('routeParamsId', params.planYearId)
    }

    setEvent('route', newPage)
    setEvent('subroute', subPage)

    refNavigate.value = !refNavigate.value

    // Same-page navigation: just refresh params and breadcrumbs without mutating
    // the back stack. This fixes lesson-detail → lesson-detail transitions where
    // the route name stays the same but the target entity changes.
    if (newPage === currentPage.value && subPage === currentSubPage.value) {
      const keysOfParams = Object.keys(params)
      keysOfParams.forEach((key) => {
        if (newPage.includes(`:${key}`)) {
          newPage = newPage.replace(`:${key}`, params[key])
        }
        navigationParams.value[key] = params[key]
      })

      persistTransformNavEvents(newPage, params)

      breadCrumbs.value = getBreadCrumbs(subPage || newPage, {
        previousEmployerPage: currentPage.value,
        previousPage: currentPage.value,
        actualEmployer,
        navigationParams: navigationParams.value
      })

      navigationMonitor.recordNavigation({
        from: currentPage.value,
        to: newPage,
        action: 'replace',
        source: 'virtual'
      })

      setEvent('isSubpage', isSubpage.value)
      return
    }

    if (newPage !== currentPage.value || subPage !== currentSubPage.value) {
      isSubpage.value = false
      const keysOfParams = Object.keys(params)

      if (!replace) {
        backStackParams.value.push({ ...navigationParams.value })
      }
      keysOfParams.forEach((key) => {
        if (newPage.includes(`:${key}`)) {
          newPage = newPage.replace(`:${key}`, params[key])
        }
        navigationParams.value[key] = params[key]
      })
      if (subPage) {
        if (newPage.includes('/')) {
          newPage = `${newPage.split('/')[0]}/${subPage}`
        } else {
          newPage = `${newPage}/${subPage}`
        }

        isSubpage.value = true
        currentSubPage.value = subPage
      }
      internalQuery.value = query
      if (!replace) {
        backStack.value.push(currentPage.value)
        backStackBreadCrumbs.value.push(breadCrumbs.value)
      }
      // Scoped fix (Terms & Conditions only): persist the previous page so the
      // dynamic ${previousPage} breadcrumb can be restored after a full page
      // refresh. No behavior change for any other route.
      if (
        subPage === routes.termsAndConditions.name ||
        newPage === routes.termsAndConditions.name
      ) {
        setEvent('termsAndConditionsPreviousPage', currentPage.value)
      }
      // Persist the Transform params needed to rebuild the view after a full page refresh
      // (navigationParams live only in memory).
      persistTransformNavEvents(newPage, params)
      breadCrumbs.value = getBreadCrumbs(subPage || newPage, {
        previousEmployerPage: currentPage.value,
        previousPage: currentPage.value,
        actualEmployer,
        navigationParams: navigationParams.value
      })
      navigationMonitor.recordNavigation({
        from: currentPage.value,
        to: newPage,
        action: replace ? 'replace' : 'push',
        source: 'virtual'
      })
      currentPage.value = newPage
      // A replace keeps the forward stack intact — only a genuine forward navigation
      // (a new page pushed onto the back stack) invalidates it.
      if (!replace) {
        forwardStack.value = []
        forwardStackBreadCrumbs.value = []
        forwardStackParams.value = []
      }

      const eventId = eventIdByPageName.get(subPage || newPage)
      if (saveEvent && eventId) {
        createLog({
          eventId,
          logName: 'user_activity_log_v2',
          employerId: get(actualEmployer, 'value.id', null),
          planYearId: get(actualEmployer, 'value.planYearId', null),
          agencyId: get(actualEmployer, 'value.agency_id', null)
        }).catch((err) => handleErrors('createLog navigation', err))
      }
    }

    setEvent('isSubpage', isSubpage.value)
  }

  function syncSessionNavigationState() {
    setEvent('route', currentPage.value)
    setEvent('subroute', currentSubPage.value)
    setEvent('isSubpage', isSubpage.value)
    setEvent('pageReloaded', false)
  }

  function goBack() {
    emitter.emit('closeBcsModal')
    if (backStack.value.length === 0) return
    navigationMonitor.recordNavigation({
      from: currentPage.value,
      to: backStack.value[backStack.value.length - 1],
      action: 'back',
      source: 'virtual'
    })
    forwardStackParams.value.push({ ...navigationParams.value })
    forwardStack.value.push(currentPage.value)
    forwardStackBreadCrumbs.value.push(breadCrumbs.value)
    currentPage.value = backStack.value.pop()
    breadCrumbs.value = backStackBreadCrumbs.value.pop()
    navigationParams.value = backStackParams.value.pop()
    if (currentPage.value.includes('/')) {
      currentSubPage.value = currentPage.value.split('/')[1]
    } else {
      currentSubPage.value = null
    }
    isSubpage.value = !!currentSubPage.value
    // Back can land outside the Transform module — the owner context must not survive
    // that (empty params: exits on non-Transform pages, untouched on Transform ones).
    syncTransformOwnerContext(currentPage.value, {})
    syncSessionNavigationState()
  }

  function goBackN(times = 1) {
    for (let i = 0; i < times; i++) {
      goBack()
    }
  }

  function goForward() {
    emitter.emit('closeBcsModal')
    if (forwardStack.value.length === 0) return
    navigationMonitor.recordNavigation({
      from: currentPage.value,
      to: forwardStack.value[forwardStack.value.length - 1],
      action: 'forward',
      source: 'virtual'
    })
    backStackParams.value.push({ ...navigationParams.value })
    backStack.value.push(currentPage.value)
    backStackBreadCrumbs.value.push(breadCrumbs.value)
    currentPage.value = forwardStack.value.pop()
    breadCrumbs.value = forwardStackBreadCrumbs.value.pop()
    navigationParams.value = forwardStackParams.value.pop()
    if (currentPage.value.includes('/')) {
      currentSubPage.value = currentPage.value.split('/')[1]
    } else {
      currentSubPage.value = null
    }
    isSubpage.value = !!currentSubPage.value
    syncTransformOwnerContext(currentPage.value, {})
    syncSessionNavigationState()
  }

  function getBreadcrumbs() {
    const backStackLength = customNavigation.value.backStack.length
    return customNavigation.value.backStack
      .slice(backStackLength - 3, backStackLength)
      .map((val) => {
        let texToShow = ''
        texToShow = val
          .replace(/([a-z])([A-Z])/g, '$1 $2')
          .replace(/^./, (str) => str.toUpperCase())
        return { route: val, display: texToShow }
      })
      .filter((val) => val.display !== 'Home')
  }

  const customNavigation = ref({
    routes,
    currentPage,
    currentSubPage,
    backStack,
    forwardStack,
    internalQuery,
    breadCrumbs,
    navigationParams,
    getDynamicRoute,
    navigate,
    goBack,
    goForward,
    getBreadcrumbs,
    goBackN
  })

  return {
    customNavigation,
    refNavigate
  }
})
